/**
 * Condition Handler - D&D 5e Condition Effects System
 *
 * Manages condition application, behavioral effects, and mechanical impacts.
 * Implements all D&D 5e conditions with proper rule enforcement.
 *
 * D&D 5e Rule Reference: Conditions (PHB p.290-292, Appendix A)
 * date_of_change: 2025-09-21
 * purpose: Implement condition mechanics per D&D 5e rules
 */

// date_of_change: 2025-10-18 - Use shared CanonicalSchemas single source of truth
import { Condition } from '../../../shared/types/CanonicalSchemas';

import RangeCalculator from '../utils/RangeCalculator';

export interface ConditionEffect {
  condition: Condition;
  source: string; // What caused the condition
  duration: number; // in rounds (-1 for indefinite)
  startRound: number;
  saveEnds?: {
    ability: 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma';
    dc: number;
    endOfTurn: boolean;
  };
  concentrationRequired?: boolean;
  casterToken?: string; // For conditions requiring concentration
}

export interface ConditionApplication {
  targetId: string;
  effect: ConditionEffect;
  applied: boolean;
  reason?: string;
}

export interface ConditionImpact {
  attackRolls: 'advantage' | 'disadvantage' | 'normal';
  abilityChecks: 'advantage' | 'disadvantage' | 'normal';
  savingThrows: 'advantage' | 'disadvantage' | 'normal';
  movementRestrictions: string[];
  actionRestrictions: string[];
  immunities: string[];
  vulnerabilities: string[];
  cannotTarget: boolean;
  cannotBeTargeted: boolean;
  specialRules: string[];
}

export interface MovementRestriction {
  canMove: boolean;
  speedModifier: number; // multiplier (0.5 = half speed, 0 = no movement)
  restrictedDirections: Array<{
    sourceId: string;
    sourceName: string;
    restriction: 'cannot_approach' | 'must_flee' | 'no_movement';
  }>;
  terrainCostMultiplier: number;
}

/**
 * Condition Handler - Manages D&D 5e condition effects and behavioral impacts
 * ENFORCES: Official D&D 5e condition rules and mechanical effects
 * INTEGRATES: With movement, combat, and targeting systems
 */
export class ConditionHandler {

  /**
   * D&D 5e Condition definitions with mechanical effects
   * @date_of_change: 2025-09-21 - Complete D&D 5e condition ruleset
   */
  static readonly CONDITION_EFFECTS: Record<Condition, ConditionImpact> = {
    blinded: {
      attackRolls: 'disadvantage',
      abilityChecks: 'disadvantage',
      savingThrows: 'normal',
      movementRestrictions: ['Cannot see, relying on hearing and other senses'],
      actionRestrictions: ['Cannot target spells or abilities that require sight'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Attack rolls against this creature have advantage']
    },

    charmed: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: [],
      actionRestrictions: ['Cannot attack the charmer or target harmful spells/abilities at charmer'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Charmer has advantage on social interaction checks']
    },

    deafened: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: [],
      actionRestrictions: ['Cannot hear, automatic failure on checks requiring hearing'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: []
    },

    frightened: {
      attackRolls: 'disadvantage',
      abilityChecks: 'disadvantage',
      savingThrows: 'normal',
      movementRestrictions: ['Cannot willingly move closer to the source of fear'],
      actionRestrictions: ['Disadvantage on attack rolls and ability checks while source is visible'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Must take Dash action if able to move away from fear source']
    },

    grappled: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: ['Speed becomes 0, cannot benefit from speed bonuses'],
      actionRestrictions: ['Can use action to escape with Athletics or Acrobatics check'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Condition ends if grappler is incapacitated or moved out of reach']
    },

    incapacitated: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: [],
      actionRestrictions: ['Cannot take actions or reactions'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Concentration automatically ends']
    },

    invisible: {
      attackRolls: 'advantage',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: [],
      actionRestrictions: [],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Attack rolls against this creature have disadvantage', 'Ends if attacks or casts harmful spell']
    },

    paralyzed: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: ['Cannot move or speak'],
      actionRestrictions: ['Cannot take actions or reactions', 'Automatically fails Strength and Dexterity saves'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Attack rolls against this creature have advantage', 'Melee hits within 5 feet are critical hits']
    },

    petrified: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: ['Cannot move or speak'],
      actionRestrictions: ['Cannot take actions or reactions', 'Unaware of surroundings'],
      immunities: ['All damage types'],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Resistance to all damage', 'Immune to poison and disease']
    },

    poisoned: {
      attackRolls: 'disadvantage',
      abilityChecks: 'disadvantage',
      savingThrows: 'normal',
      movementRestrictions: [],
      actionRestrictions: [],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: []
    },

    prone: {
      attackRolls: 'disadvantage',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: ['Can only crawl or use action to stand up', 'Crawling costs 1 extra foot per foot moved'],
      actionRestrictions: ['Must use half movement to stand up'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Ranged attacks have disadvantage', 'Melee attacks within 5 feet have advantage']
    },

    restrained: {
      attackRolls: 'disadvantage',
      abilityChecks: 'normal',
      savingThrows: 'disadvantage',
      movementRestrictions: ['Speed becomes 0, cannot benefit from speed bonuses'],
      actionRestrictions: ['Disadvantage on Dexterity saving throws'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Attack rolls against this creature have advantage']
    },

    stunned: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: ['Cannot move'],
      actionRestrictions: ['Cannot take actions or reactions', 'Can speak falteringly', 'Automatically fails Strength and Dexterity saves'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Attack rolls against this creature have advantage']
    },

    unconscious: {
      attackRolls: 'normal',
      abilityChecks: 'normal',
      savingThrows: 'normal',
      movementRestrictions: ['Cannot move or speak'],
      actionRestrictions: ['Cannot take actions or reactions', 'Unaware of surroundings', 'Automatically fails Strength and Dexterity saves'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Attack rolls have advantage', 'Melee hits within 5 feet are critical hits', 'Falls prone']
    },

    exhausted: {
      attackRolls: 'normal', // Varies by level
      abilityChecks: 'disadvantage', // Level 1+
      savingThrows: 'normal',
      movementRestrictions: ['Speed halved at level 2+', 'Cannot move at level 5+'],
      actionRestrictions: ['Disadvantage on attack rolls and saves at level 3+', 'Hit point maximum halved at level 4+'],
      immunities: [],
      vulnerabilities: [],
      cannotTarget: false,
      cannotBeTargeted: false,
      specialRules: ['Death at level 6', 'Multiple exhaustion levels stack']
    }
  };

  /**
   * Apply condition to a target
   * @param targetId - Target to apply condition to
   * @param condition - Condition to apply
   * @param source - Source of the condition
   * @param duration - Duration in rounds (-1 for indefinite)
   * @param currentRound - Current combat round
   * @param options - Additional condition options
   * @returns Application result
   */
  static applyCondition(
    targetId: string,
    condition: Condition,
    source: string,
    duration: number = -1,
    currentRound: number = 0,
    options: {
      saveEnds?: ConditionEffect['saveEnds'];
      concentrationRequired?: boolean;
      casterToken?: string;
    } = {}
  ): ConditionApplication {

    const effect: ConditionEffect = {
      condition,
      source,
      duration,
      startRound: currentRound,
      saveEnds: options.saveEnds,
      concentrationRequired: options.concentrationRequired,
      casterToken: options.casterToken
    };

    // Log condition application
    console.log(`[ConditionHandler] Applying ${condition} to ${targetId} from ${source} (duration: ${duration === -1 ? 'indefinite' : duration + ' rounds'})`);

    return {
      targetId,
      effect,
      applied: true
    };
  }

  /**
   * Remove condition from a target
   * @param targetId - Target to remove condition from
   * @param condition - Condition to remove
   * @param reason - Reason for removal
   * @returns Removal result
   */
  static removeCondition(
    targetId: string,
    condition: Condition,
    reason: string = 'Condition ended'
  ): {
    targetId: string;
    condition: Condition;
    removed: boolean;
    reason: string;
  } {
    console.log(`[ConditionHandler] Removing ${condition} from ${targetId}: ${reason}`);

    return {
      targetId,
      condition,
      removed: true,
      reason
    };
  }

  /**
   * Get movement restrictions for a token based on active conditions
   * @param token - Token to check
   * @param activeConditions - Currently active condition effects
   * @param battleState - Current battle state for fear source positions
   * @returns Movement restrictions
   */
  static getMovementRestrictions(
    token: { id: string; x?: number; y?: number },
    activeConditions: ConditionEffect[],
    battleState: { tokens?: Array<{ id: string; x?: number; y?: number; name?: string }> } = {}
  ): MovementRestriction {

    const restrictions: MovementRestriction = {
      canMove: true,
      speedModifier: 1.0,
      restrictedDirections: [],
      terrainCostMultiplier: 1.0
    };

    for (const effect of activeConditions) {

      switch (effect.condition) {
        case 'grappled':
        case 'restrained':
          restrictions.canMove = false;
          restrictions.speedModifier = 0;
          break;

        case 'paralyzed':
        case 'petrified':
        case 'stunned':
        case 'unconscious':
          restrictions.canMove = false;
          restrictions.speedModifier = 0;
          break;

        case 'prone':
          restrictions.speedModifier *= 0.5; // Crawling is half speed
          restrictions.terrainCostMultiplier *= 2; // Extra foot per foot
          break;

        case 'frightened':
          // Find the fear source
          const fearSource = this.findFearSource(effect.source, battleState.tokens || []);
          if (fearSource && token.x !== undefined && token.y !== undefined) {
            restrictions.restrictedDirections.push({
              sourceId: fearSource.id,
              sourceName: fearSource.name || 'Unknown',
              restriction: 'cannot_approach'
            });
          }
          break;

        case 'exhausted':
          // Exhaustion level would need to be tracked separately
          // For now, assume level 2+ (speed halved)
          restrictions.speedModifier *= 0.5;
          break;
      }
    }

    return restrictions;
  }

  /**
   * Check if movement is valid considering conditions
   * @param token - Token attempting to move
   * @param targetPosition - Desired destination
   * @param currentConditions - Active conditions on the token
   * @param battleState - Current battle state
   * @param options - Movement calculation options
   * @returns Movement validation result
   */
  static validateMovement(
    token: { id: string; x?: number; y?: number },
    targetPosition: { x: number; y: number },
    currentConditions: ConditionEffect[],
    battleState: any = {},
    options: any = {}
  ): {
    allowed: boolean;
    reasons: string[];
    modifiedSpeed?: number;
  } {

    const result = {
      allowed: true,
      reasons: [] as string[],
      modifiedSpeed: undefined as number | undefined
    };

    const restrictions = this.getMovementRestrictions(token, currentConditions, battleState);

    // Check if movement is completely blocked
    if (!restrictions.canMove) {
      result.allowed = false;
      const blockingConditions = currentConditions
        .filter(effect => ['grappled', 'restrained', 'paralyzed', 'petrified', 'stunned', 'unconscious'].includes(effect.condition))
        .map(effect => effect.condition);
      result.reasons.push(`Movement blocked by condition(s): ${blockingConditions.join(', ')}`);
      return result;
    }

    // Check direction restrictions (fear effects)
    for (const restriction of restrictions.restrictedDirections) {
      if (restriction.restriction === 'cannot_approach') {
        const fearSource = battleState.tokens?.find((t: any) => t.id === restriction.sourceId);
        if (fearSource && this.isMovingCloser(token, targetPosition, fearSource, options)) {
          result.allowed = false;
          result.reasons.push(`Cannot move closer to ${restriction.sourceName} while frightened`);
        }
      }
    }

    // Apply speed modifiers
    if (restrictions.speedModifier < 1.0) {
      result.modifiedSpeed = restrictions.speedModifier;
    }

    return result;
  }

  /**
   * Get attack roll modifiers from conditions
   * @param attacker - Attacking token
   * @param target - Target being attacked
   * @param attackerConditions - Attacker's active conditions
   * @param targetConditions - Target's active conditions
   * @returns Attack roll modifications
   */
  static getAttackModifiers(
    attacker: { id: string },
    target: { id: string },
    attackerConditions: ConditionEffect[],
    targetConditions: ConditionEffect[]
  ): {
    advantage: boolean;
    disadvantage: boolean;
    cannotAttack: boolean;
    reasons: string[];
  } {

    const result = {
      advantage: false,
      disadvantage: false,
      cannotAttack: false,
      reasons: [] as string[]
    };

    // Check attacker conditions
    for (const effect of attackerConditions) {
      const conditionRules = this.CONDITION_EFFECTS[effect.condition];

      if (conditionRules.attackRolls === 'disadvantage') {
        result.disadvantage = true;
        result.reasons.push(`Disadvantage from ${effect.condition}`);
      }

      if (conditionRules.attackRolls === 'advantage') {
        result.advantage = true;
        result.reasons.push(`Advantage from ${effect.condition}`);
      }

      // Check for action restrictions
      if (conditionRules.actionRestrictions.some(restriction =>
        restriction.includes('Cannot take actions') ||
        restriction.includes('Cannot attack'))) {
        result.cannotAttack = true;
        result.reasons.push(`Cannot attack due to ${effect.condition}`);
      }
    }

    // Check target conditions that affect incoming attacks
    for (const effect of targetConditions) {
      // Conditions that give attackers advantage
      if (['blinded', 'paralyzed', 'restrained', 'stunned', 'unconscious'].includes(effect.condition)) {
        result.advantage = true;
        result.reasons.push(`Advantage attacking ${effect.condition} target`);
      }

      // Invisible targets impose disadvantage
      if (effect.condition === 'invisible') {
        result.disadvantage = true;
        result.reasons.push(`Disadvantage attacking invisible target`);
      }

      // Prone targets: advantage for melee, disadvantage for ranged
      if (effect.condition === 'prone') {
        // This would need attack range context to determine melee vs ranged
        result.reasons.push(`Target is prone (affects attack based on range)`);
      }
    }

    return result;
  }

  /**
   * Process end-of-turn condition saves
   * @param tokenId - Token making saves
   * @param activeConditions - Conditions that might end
   * @param tokenStats - Token's ability scores for saves
   * @returns Array of conditions that ended
   */
  static processEndOfTurnSaves(
    tokenId: string,
    activeConditions: ConditionEffect[],
    tokenStats: { abilities?: { [key: string]: number } }
  ): Array<{
    condition: Condition;
    saveRoll: number;
    success: boolean;
    ended: boolean;
  }> {

    const saveResults: ReturnType<typeof this.processEndOfTurnSaves> = [];

    for (const effect of activeConditions) {
      if (effect.saveEnds && effect.saveEnds.endOfTurn) {
        const abilityScore = tokenStats.abilities?.[effect.saveEnds.ability] || 10;
        const modifier = Math.floor((abilityScore - 10) / 2);
        const saveRoll = Math.floor(Math.random() * 20) + 1 + modifier;
        const success = saveRoll >= effect.saveEnds.dc;

        saveResults.push({
          condition: effect.condition,
          saveRoll,
          success,
          ended: success
        });

        if (success) {
          console.log(`[ConditionHandler] ${tokenId} saved against ${effect.condition} (rolled ${saveRoll} vs DC ${effect.saveEnds.dc})`);
        }
      }
    }

    return saveResults;
  }

  /**
   * Find the source of a fear effect for movement restrictions
   * @param source - Source description from condition
   * @param tokens - Available tokens in battle
   * @returns Fear source token or null
   */
  private static findFearSource(
    source: string,
    tokens: Array<{ id: string; x?: number; y?: number; name?: string }>
  ): { id: string; x?: number; y?: number; name?: string } | null {

    // Extract token name/id from source string like "Turn Undead (Cleric Name)"
    const sourceMatch = source.match(/\((.*?)\)/);
    if (sourceMatch) {
      const sourceName = sourceMatch[1];
      return tokens.find(token =>
        token.name === sourceName ||
        token.id === sourceName
      ) || null;
    }

    return null;
  }

  /**
   * Check if movement brings token closer to a fear source
   * @param token - Token moving
   * @param targetPosition - Intended destination
   * @param fearSource - Source of fear
   * @param options - Distance calculation options
   * @returns True if moving closer
   */
  private static isMovingCloser(
    token: { x?: number; y?: number },
    targetPosition: { x: number; y: number },
    fearSource: { x?: number; y?: number },
    options: any = {}
  ): boolean {

    if (token.x === undefined || token.y === undefined ||
        fearSource.x === undefined || fearSource.y === undefined) {
      return false; // Cannot determine without positions
    }

    const currentDistance = RangeCalculator.calculateGridDistance(
      { x: token.x, y: token.y },
      { x: fearSource.x, y: fearSource.y },
      'square',
      5
    ) as { feet: number; cells: number; isDiagonal: boolean; horizontal: number; vertical: number };

    const newDistance = RangeCalculator.calculateGridDistance(
      targetPosition,
      { x: fearSource.x, y: fearSource.y },
      'square',
      5
    ) as { feet: number; cells: number; isDiagonal: boolean; horizontal: number; vertical: number };

    return newDistance.feet < currentDistance.feet;
  }

  /**
   * Get human-readable condition effects for display
   * @param conditions - Active condition effects
   * @returns Display-ready condition information
   */
  static getDisplayData(conditions: ConditionEffect[]): Array<{
    condition: Condition;
    source: string;
    duration: string;
    effects: string[];
    canSave: boolean;
  }> {

    return conditions.map(effect => {
      const conditionRules = this.CONDITION_EFFECTS[effect.condition];
      const effects: string[] = [];

      // Collect all mechanical effects
      if (conditionRules.attackRolls !== 'normal') {
        effects.push(`Attack rolls: ${conditionRules.attackRolls}`);
      }
      if (conditionRules.abilityChecks !== 'normal') {
        effects.push(`Ability checks: ${conditionRules.abilityChecks}`);
      }
      effects.push(...conditionRules.movementRestrictions);
      effects.push(...conditionRules.actionRestrictions);
      effects.push(...conditionRules.specialRules);

      return {
        condition: effect.condition,
        source: effect.source,
        duration: effect.duration === -1 ? 'Indefinite' : `${effect.duration} rounds`,
        effects,
        canSave: !!effect.saveEnds
      };
    });
  }
}